import React, { forwardRef, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
  Slide,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import VideoCameraFrontOutlinedIcon from '@mui/icons-material/VideoCameraFrontOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EmojiPeopleOutlinedIcon from '@mui/icons-material/EmojiPeopleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { TransitionProps } from '@mui/material/transitions';
import { formatEpochToDate } from '../../../Common/Date/ConvertDate';
import { useNavigate, useParams } from 'react-router-dom';
import { getCandidatesByStatus, getCandidateSummaryByJobNum } from '../jobManagemnetApi';
import CandidatePopover from './CandidatePopover';
import { theme } from '../../../../theme';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const mockCandidates = [
  { name: 'Annette Black' },
  { name: 'Darrell Steward' },
  { name: 'Ralph Edwards' },
  { name: 'Cameron Williamson' },
  { name: 'Floyd Miles' },
  { name: 'Jacob Jones' },
  { name: 'Annette Black' },
  { name: 'Darrell Steward' },
  { name: 'Ralph Edwards' },
  { name: 'Cameron Williamson' },
  { name: 'Floyd Miles' },
  { name: 'Jacob Jones' },
];


interface CandidateSummaryType {
  CandidateStatusKey: string;
  CandidateCount: number;
}

export interface CandidateByStageType {
  CandidateName: string;
  MapID: number;
  CandidateStatus:string
}

interface JobDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  jobTitle: string;
  createdBy: string;
  createdDate: string;
  jobNum: number | null;
}

const stageIcons: { [key: string]: React.ReactNode } = {
  'All Candidates': <PeopleAltOutlinedIcon />,
  Screening: <SearchOutlinedIcon />,
  Shortlisted: <CheckCircleOutlineOutlinedIcon />,
  'On Hold': <PauseCircleOutlineIcon />,
  'HR Interview': <VideoCameraFrontOutlinedIcon />,
  Assessment: <AssignmentOutlinedIcon />,
  'Panel Interview': <GroupsOutlinedIcon />,
  'Preboarding': <EmojiPeopleOutlinedIcon />,
  Rejected: <CancelOutlinedIcon />,
};

const JobDetailsDialog: React.FC<JobDetailsDialogProps> = ({
  open,
  onClose,
  jobTitle,
  createdBy,
  createdDate,
  jobNum,
}) => {
  const { tenant } = useParams<{ tenant: string }>();
  const [candidateSummaryData, setCandidateSummaryData] = useState<CandidateSummaryType[]>([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [candidatesByStage, setCandidatesByStage] = useState<CandidateByStageType[]>([])
  const [loading, setLoading] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const navigate = useNavigate();


  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [showStatus,setShowStatus] =useState(false)

  const handleStageClick = async (event: React.MouseEvent<HTMLElement>, stage: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedStage(stage);
    setLoadingCandidates(true); // start loading

    const payLoad = {
      JobNum: jobNum,
      CandidateStatusKey: stage
    }
    try {
      const response = await getCandidatesByStatus(tenant, payLoad)
      setCandidatesByStage(response)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingCandidates(false)
    }

  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedStage(null);
  };

  const isPopoverOpen = Boolean(anchorEl);


  const fetchCandidateSummary = async () => {
    setLoading(true);
    try {
      const result = await getCandidateSummaryByJobNum(tenant, { JobNum: jobNum });
      if (result) {
        setCandidateSummaryData(result.Data || []);
        setTotalCandidates(result.Total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && jobNum) {
      fetchCandidateSummary();
    }
  }, [open, jobNum]);

  const navigateToHiringPipeline =async(mapId:number)=>{
    navigate(`/${tenant}/hiringpipeline`, {
    state: {
      jobNum,
      mapId,
    },
  });
  }

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)", } }}
      PaperProps={{
        sx: {
          width: '22vw',
          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            {jobTitle}
          </Typography>
          <IconButton
          edge="end"
          color="inherit"
          sx={{ position: 'relative', width: '2vw', height: '4vh' }}
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {`Created by ${createdBy}, ${formatEpochToDate(createdDate)}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Job Details
        </Typography> */}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        ) : candidateSummaryData.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" mt={4}>
            No candidates found for this job.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {/* All Candidates */}
            <Box
              onClick={(e) => {handleStageClick(e, 'All Candidates');setShowStatus(true)}}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 1,
                py: 1,
                borderRadius: 2,
                border: selectedStage === 'All Candidates' ? `1px solid ${theme.palette.primary.dark} ` : '1px solid transparent',
                '&:hover': {
                  backgroundColor: '#e9e3f7',
                  cursor: 'pointer',
                },
              }}
            >
              <IconButton sx={{ color: '#9C6ADE', backgroundColor: '#F4F0FA' }}>
                <GroupsOutlinedIcon />
              </IconButton>
              <Box>
                <Typography variant='subtitle2' fontWeight={500}>All Candidates</Typography>
                <Typography variant="caption" color="text.secondary">
                  {totalCandidates} {totalCandidates === 1 ? 'candidate' : 'candidates'}
                </Typography>
              </Box>
            </Box>

            {/* Individual Stages */}
            {candidateSummaryData.map((stage, index) => (
              <Box
                key={index}
                onClick={(e) => handleStageClick(e, stage.CandidateStatusKey)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 1,
                  py: 1,
                  borderRadius: 2,
                  transition: 'background 0.3s',
                  border: selectedStage === stage.CandidateStatusKey ? `1px solid ${theme.palette.primary.dark} ` : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: '#e9e3f7',
                    cursor: 'pointer',
                  },
                }}
              >
                <IconButton sx={{ color: '#9C6ADE', backgroundColor: '#F4F0FA' }}>
                  {stageIcons[stage.CandidateStatusKey] || <PeopleAltOutlinedIcon />}
                </IconButton>
                <Box>
                  <Typography variant='subtitle2' fontWeight={500}>{stage.CandidateStatusKey}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stage.CandidateCount} {stage.CandidateCount === 1 ? 'candidate' : 'candidates'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <CandidatePopover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        candidates={candidatesByStage}
        loading={loadingCandidates}
        navigateToHiringPipeline={navigateToHiringPipeline}
        showStatus={showStatus}
      />

    </Dialog>
  );
};

export default JobDetailsDialog;
